import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { analyses as analysesApi } from '@/api/analyses';
import { useRequireOrg } from '@/tenancy/OrgContext';
import PageHeader from '@/components/PageHeader';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';
import { CREDITS_QUERY_KEY } from '@/components/CreditsWidget';
import { useConfirm } from '@/components/ConfirmDialog';
import { formatDateTime } from '@/lib/format';
import { formatEstimate, formatBand } from '@/lib/estimate';
import { useEstimateMode } from '@/estimate/EstimateModeContext';
import { StatusBadge } from './AnalysesPage';

export default function AnalysisDetailPage() {
  const { id: orgId, org } = useRequireOrg();
  const canDelete = org.role === 'OWNER' || org.role === 'ADMIN';
  const { mode } = useEstimateMode();
  const confirm = useConfirm();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['analysis', orgId, id],
    queryFn: () => analysesApi.get(orgId, id!),
    enabled: !!id,
  });

  const retryMutation = useMutation({
    mutationFn: () => analysesApi.retry(orgId, id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['analysis', orgId, id] });
      qc.invalidateQueries({ queryKey: ['analyses', orgId] });
      // Retry charges 1 credit on success — refresh the widget so the user sees it.
      qc.invalidateQueries({ queryKey: [...CREDITS_QUERY_KEY, orgId] });
      qc.invalidateQueries({ queryKey: ['billing', 'history', orgId] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => analysesApi.remove(orgId, id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['analyses', orgId] });
      qc.invalidateQueries({ queryKey: ['dashboard', orgId] });
      navigate('/analyses');
    },
  });

  const setPrimaryMutation = useMutation({
    mutationFn: (issueKey: string | null) => analysesApi.setPrimaryJiraIssue(orgId, id!, issueKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['analysis', orgId, id] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'work-items', orgId] });
      qc.invalidateQueries({ queryKey: ['analyses-work-items', orgId] });
    },
  });

  const handleDelete = async () => {
    if (!data) return;
    const ok = await confirm({
      title: 'Delete analysis',
      message: `Delete analysis for PR #${data.prNumber} from history?\n\nThe analysis will be hidden from lists and metrics. Credits will NOT be refunded.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (ok) removeMutation.mutate();
  };

  return (
    <>
      <PageHeader
        title={data ? `#${data.prNumber} — ${data.prTitle}` : 'Analysis'}
        subtitle={
          data ? (
            <>
              {data.repositoryFullName} ·{' '}
              <a href={data.prUrl} target="_blank" rel="noreferrer" className="link">
                Open on GitHub ↗
              </a>
            </>
          ) : null
        }
        actions={
          data ? (
            <>
              <StatusBadge status={data.status} />
              <button
                className="btn"
                disabled={data.status === 'IN_PROGRESS' || retryMutation.isPending}
                onClick={() => retryMutation.mutate()}
              >
                {retryMutation.isPending ? 'Scheduling…' : 'Retry analysis'}
              </button>
              {canDelete ? (
                <button
                  className="btn btn-danger"
                  disabled={data.status === 'IN_PROGRESS' || removeMutation.isPending}
                  onClick={handleDelete}
                  title="Soft-delete this analysis from history"
                >
                  {removeMutation.isPending ? 'Deleting…' : 'Delete'}
                </button>
              ) : null}
            </>
          ) : null
        }
      />

      <p>
        <Link to="/analyses" className="link">
          ← Back to list
        </Link>
      </p>

      {isLoading ? <Spinner label="Loading analysis…" /> : null}
      {error ? <ErrorBanner error={error} onRetry={() => refetch()} /> : null}
      {retryMutation.error ? <ErrorBanner error={retryMutation.error} /> : null}
      {removeMutation.error ? <ErrorBanner error={removeMutation.error} /> : null}

      {data ? (
        <>
          <section className="metric-grid">
            <KV label="Author" value={`@${data.prAuthor}`} />
            <KV label="Created" value={formatDateTime(data.prCreatedAt)} />
            <KV label="Merged" value={formatDateTime(data.prMergedAt)} />
            <KV label="Base branch" value={data.prBaseBranch ?? '—'} />
            <KV label="Attempts" value={String(data.attempts)} />
          </section>

          {data.status === 'SKIPPED_BRANCH_EXCLUDED' ? (
            <section className="card skip-cta">
              <h3>This pull request wasn't analyzed</h3>
              <p className="muted">
                Its base branch{data.prBaseBranch ? <> (<code>{data.prBaseBranch}</code>)</> : null} isn't
                in this project's analyzed branches, so it was saved but skipped. Hit{' '}
                <strong>Retry analysis</strong> above to analyze it anyway — we'll charge 1 credit.
                To analyze this branch automatically in future, add it under{' '}
                <strong>Projects → settings</strong>.
              </p>
            </section>
          ) : null}

          {data.status === 'SKIPPED_NO_CREDITS' ? (
            <section className="card card-amber skip-cta">
              <h3>Top up credits to analyze this pull request</h3>
              <p className="muted">
                This pull request was received but skipped because your organization
                had zero credits at the time. Buy a credit pack and hit{' '}
                <strong>Retry analysis</strong> above — we will charge 1 credit and run
                the analysis.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/billing')}
              >
                Buy credits
              </button>
            </section>
          ) : null}

          <section className="card">
            <h3>Effort</h3>
            <p className="muted small" style={{ marginTop: 0 }}>
              Actual hours live on the work item this PR rolls up to — open it from the
              Work items view on the Analyses page.
            </p>
            <div className="metric-grid">
              <KV
                label="AI-assisted estimate"
                value={formatEstimate(data.estimatedHoursMin, data.estimatedHoursLikely, data.estimatedHoursMax, mode)}
                sub={`Band ${formatBand(data.estimatedHoursMin, data.estimatedHoursMax)}`}
              />
              <KV
                label="No-AI estimate"
                value={formatEstimate(data.unassistedHoursMin, data.unassistedHoursLikely, data.unassistedHoursMax, mode)}
                sub={`Band ${formatBand(data.unassistedHoursMin, data.unassistedHoursMax)}`}
              />
              <KV label="Complexity" value={data.complexity ?? '—'} />
              <KV label="AI suitability" value={data.aiAssistanceSuitability ?? '—'} />
              <KV label="Confidence" value={data.confidence ?? '—'} />
              <KV
                label="Comment posted"
                value={data.commentPostedToGithub ? 'Yes' : 'No'}
              />
            </div>
          </section>

          {data.summary ? (
            <section className="card">
              <h3>Summary</h3>
              <p>{data.summary}</p>
            </section>
          ) : null}

          {data.mainFactors ? (
            <section className="card">
              <h3>Main factors</h3>
              <pre className="multiline">{data.mainFactors}</pre>
            </section>
          ) : null}

          {data.risks ? (
            <section className="card">
              <h3>Risks</h3>
              <pre className="multiline">{data.risks}</pre>
            </section>
          ) : null}

          {data.jiraIssues.length > 0 ? (
            <section className="card">
              <h3>Jira issues</h3>
              <p className="muted small" style={{ marginTop: 0 }}>
                Pick which issue this PR rolls up to in Work Item analytics. Other linked
                issues stay visible but don't influence dashboard aggregates.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {data.jiraIssues.map(link => {
                  const isPrimary = data.primaryJiraIssueKey === link.issueKey;
                  return (
                    <li key={link.issueKey} style={{ padding: '0.4rem 0', borderTop: '1px solid var(--border)' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="primary-jira-issue"
                          checked={isPrimary}
                          disabled={setPrimaryMutation.isPending}
                          onChange={() => setPrimaryMutation.mutate(link.issueKey)}
                        />
                        <span style={{ flex: 1 }}>
                          <strong>{link.issueKey}</strong>
                          {link.summary ? <> — {link.summary}</> : null}
                          {isPrimary ? (
                            <span className="badge badge-info" style={{ marginLeft: '0.5rem' }}>Primary</span>
                          ) : null}
                        </span>
                        <span className="muted small">{link.source.replace('_', ' ').toLowerCase()}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
              {setPrimaryMutation.error ? (
                <div style={{ marginTop: '0.5rem' }}>
                  <ErrorBanner error={setPrimaryMutation.error} />
                </div>
              ) : null}
            </section>
          ) : null}

          {data.workBreakdown ? (
            <section className="card">
              <h3>Work breakdown</h3>
              <pre className="multiline">{data.workBreakdown}</pre>
            </section>
          ) : null}

          <section className="card">
            <h3>PR metrics</h3>
            <div className="metric-grid">
              {Object.entries(data.metrics).map(([k, v]) => (
                <KV key={k} label={humanize(k)} value={v == null ? '—' : String(v)} />
              ))}
            </div>
          </section>

          {data.errorMessage ? (
            <section className="card card-warn">
              <h3>Error</h3>
              <pre className="multiline">{data.errorMessage}</pre>
            </section>
          ) : null}
        </>
      ) : null}
    </>
  );
}

function KV({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="metric">
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      {sub ? <div className="muted small" style={{ marginTop: '0.25rem' }}>{sub}</div> : null}
    </div>
  );
}

function humanize(camel: string): string {
  return camel
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, c => c.toUpperCase())
    .trim();
}
